import React, { useState, useRef } from 'react';
import { Upload, Download, FileText, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useCredits } from '../contexts/CreditContext';
import { getApiUrl } from '../config';

function FAQGenerator() {
  const { updateCreditsFromResponse } = useCredits();
  const [files, setFiles] = useState([]);
  const [outputFormat, setOutputFormat] = useState('docx');
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [error, setError] = useState('');
  const [runClaudeFeedback, setRunClaudeFeedback] = useState(false);

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
    setError('');
    setDownloadUrl('');
  };

  const handleFormatChange = (e) => {
    setOutputFormat(e.target.value);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!files.length) {
      setError('Please upload at least one file.');
      return;
    }
    setIsGenerating(true);
    setError('');
    setDownloadUrl('');
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      formData.append('output_format', outputFormat);
      formData.append('run_claude_feedback', runClaudeFeedback);
      const response = await axios.post(getApiUrl('/generate_faq'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      setDownloadUrl(response.data.downloadUrl);
      // Update credits from response
      updateCreditsFromResponse(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate FAQ. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Technical FAQ Generator
          </h2>
          <p className="text-gray-600 text-lg">
            Upload your source materials and generate a technical FAQ for sellers. Choose your preferred output format.
          </p>
        </div>

        <form onSubmit={handleGenerate} className="space-y-6">
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Source Materials
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                accept=".pdf,.docx,.pptx,.txt,.html,.htm"
                className="hidden"
                id="faq-file-upload"
              />
              <label
                htmlFor="faq-file-upload"
                className="cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Choose Files
              </label>
              <p className="mt-2 text-sm text-gray-500">
                Supported formats: PDF, DOCX, PPTX, TXT, HTML
              </p>
              {files.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700">Selected files:</p>
                  <ul className="mt-2 text-sm text-gray-600">
                    {files.map((file, index) => (
                      <li key={index} className="flex items-center justify-between">
                        <span>{file.name}</span>
                        <span className="text-xs text-gray-500">
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Output Format */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Output Format
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="docx"
                  checked={outputFormat === 'docx'}
                  onChange={handleFormatChange}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">DOCX</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="pdf"
                  checked={outputFormat === 'pdf'}
                  onChange={handleFormatChange}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">PDF</span>
              </label>
            </div>
          </div>

          {/* Claude Feedback Toggle */}
          <div>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={runClaudeFeedback}
                onChange={(e) => setRunClaudeFeedback(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-gray-700">
                Run AI Quality Feedback (Claude)
              </span>
            </label>
            <p className="mt-1 text-xs text-gray-500">
              When enabled, the generated FAQ will be evaluated by Claude for quality assessment
            </p>
          </div>

          {/* Generate Button */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={isGenerating || files.length === 0}
              className={`px-8 py-3 text-lg font-medium rounded-md text-white ${
                isGenerating || files.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
              }`}
            >
              {isGenerating ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating FAQ...
                </div>
              ) : (
                'Generate FAQ'
              )}
            </button>
          </div>
        </form>

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {downloadUrl && (
          <div className="mt-6 text-center">
            <a
              href={downloadUrl}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              download
            >
              <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download FAQ ({outputFormat.toUpperCase()})
            </a>
          </div>
        )}

        {/* Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="bg-blue-100 rounded-full w-12 h-12 mx-auto mb-4 flex items-center justify-center">
              <span className="text-2xl font-bold text-blue-600">@</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">AI-Powered Answers</h3>
            <p className="text-gray-600 text-sm">
              Get instant, accurate technical answers from your uploaded materials using advanced AI.
            </p>
          </div>

          <div className="text-center">
            <div className="bg-green-100 rounded-full p-3 w-12 h-12 mx-auto mb-4 flex items-center justify-center">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Flexible Output</h3>
            <p className="text-gray-600 text-sm">
              Download your FAQ in DOCX or PDF format for easy sharing and editing.
            </p>
          </div>

          <div className="text-center">
            <div className="bg-purple-100 rounded-full p-3 w-12 h-12 mx-auto mb-4 flex items-center justify-center">
              <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Professional Format</h3>
            <p className="text-gray-600 text-sm">
              Download FAQs in DOCX or PDF format ready for your sales and technical teams.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FAQGenerator; 